import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  Fade,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme, keyframes } from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  useChatThreads,
  useChatThread,
  useSendChatMessage,
  useDeleteChatThread,
  useChatUsage,
} from '../hooks/useChat';
import { ChatMessage, ChatStreamEvent, ChatThread, ChatUsage, ChatUsageLimit } from '../types/api';
import { ChatStreamError } from '../services/api';
import {
  PERIOD_CREDITS,
  PERIOD_LABELS,
  USAGE_WARNING_RATIO,
  formatCredits,
  formatReset,
  tightestLimit,
  usageBlockedReason,
  usedRatio,
} from '../utils/chatUsage';
import { useToast } from '../components/ToastProvider';
import ConfirmDialog from '../components/ConfirmDialog';
import { getApiErrorMessage } from '../utils/errors';
import { formatRelativeDay } from '../utils/datetime';

const SUGGESTIONS = [
  'Create a task to review the quarterly report, high priority, due Friday',
  'What are my open tasks for this week?',
  'Log 2 hours against my "API integration" task for today',
  'Create a "Personal" category',
];

// Cap the message length in the composer to match the backend guardrail
// (AI_CHAT_MAX_CHARS, default 4000) so oversized prompts are stopped before
// they ever hit the LLM. See ISSUES.md #18.
const MAX_MESSAGE_CHARS = 4000;

// A rejected message stays on screen, marked "Not sent", for this long before it
// collapses away (its text is already back in the composer).
const REJECTED_HOLD_MS = 1400;
const COLLAPSE_MS = 320;

/** An optimistic message shown before (or instead of) the server's history. */
type LocalMessage = ChatMessage & {
  localId: number;
  /** server history length when it was added: only later server messages can be its copy */
  serverCount: number;
  /** rejected: marked "Not sent"; leaving: collapsing out, removed when done */
  status?: 'rejected' | 'leaving';
  /** a finished streamed reply: already on screen, so it doesn't animate in again */
  streamed?: boolean;
};

type ToolStatus = 'running' | 'done' | 'failed';

/**
 * One assistant message of the reply being streamed: its text so far and the
 * tools it called. A token after a tool call starts the next step, the same
 * split the saved history has.
 */
interface LiveStep {
  text: string;
  tools: { id: string; name: string; status: ToolStatus }[];
}

/** The live steps after one stream event (a new array; steps are never mutated). */
function applyStreamEvent(steps: LiveStep[], event: ChatStreamEvent): LiveStep[] {
  const last = steps[steps.length - 1];
  switch (event.event) {
    case 'token':
      if (!last || last.tools.length) return [...steps, { text: event.data.text, tools: [] }];
      return [...steps.slice(0, -1), { ...last, text: last.text + event.data.text }];
    case 'tool_call': {
      const tool = { id: event.data.id, name: event.data.name, status: 'running' as const };
      if (!last) return [{ text: '', tools: [tool] }];
      return [...steps.slice(0, -1), { ...last, tools: [...last.tools, tool] }];
    }
    case 'tool_result': {
      const status: ToolStatus = event.data.success ? 'done' : 'failed';
      return steps.map((step) =>
        step.tools.some((t) => t.id === event.data.id)
          ? { ...step, tools: step.tools.map((t) => (t.id === event.data.id ? { ...t, status } : t)) }
          : step
      );
    }
    default:
      return steps;
  }
}

const liveStepMessage = (step: LiveStep): ChatMessage => ({
  role: 'assistant',
  content: step.text,
  tool_calls: step.tools.map(({ id, name }) => ({ id, name })),
});

/** Agent message content may be a plain string or a list of content blocks. */
function contentToText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((block) => {
        if (typeof block === 'string') return block;
        if (block && typeof block === 'object' && 'text' in block) return String((block as any).text);
        return '';
      })
      .join('');
  }
  return '';
}

function threadTitle(thread: ChatThread): string {
  if (thread.chat_title) return thread.chat_title;
  const firstUserMessage = thread.chat_history.find((m) => m.role === 'user');
  const text = contentToText(firstUserMessage?.content).trim();
  if (text) return text.length > 60 ? `${text.slice(0, 60)}…` : text;
  return 'New conversation';
}

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.9); }
  40% { opacity: 1; transform: scale(1); }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-4px); }
  40%, 80% { transform: translateX(4px); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const noMotion = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };

function ToolChip({ name, status }: { name: string; status?: ToolStatus }) {
  const label = name.replace(/_/g, ' ');
  const icon =
    status === 'running' ? (
      <CircularProgress size={14} color="inherit" />
    ) : status === 'failed' ? (
      <ErrorOutlineRoundedIcon />
    ) : status === 'done' ? (
      <CheckCircleRoundedIcon />
    ) : (
      <BuildCircleRoundedIcon />
    );
  const color = status === 'failed' ? 'error' : status === 'done' ? 'success' : 'secondary';
  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      variant="outlined"
      color={color}
      aria-label={status === 'running' ? `Running ${label}` : status === 'failed' ? `${label} failed` : label}
      sx={{ '& .MuiChip-icon': { ml: '6px' } }}
    />
  );
}

function TypingIndicator() {
  return (
    <Stack direction="row" spacing={0.6} sx={{ px: 2, py: 1.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            animation: `${pulse} 1.2s ${i * 0.18}s infinite ease-in-out`,
          }}
        />
      ))}
    </Stack>
  );
}

function MessageBubble({
  message,
  rejected = false,
  animateIn = false,
  toolStatuses,
  streaming = false,
}: {
  message: ChatMessage;
  rejected?: boolean;
  animateIn?: boolean;
  /** live status per tool call id, while the reply streams */
  toolStatuses?: Record<string, ToolStatus>;
  /** text is still arriving: show a caret after it */
  streaming?: boolean;
}) {
  const isUser = message.role === 'user';
  // a streaming step keeps its trailing whitespace so words don't run together
  const text = streaming ? contentToText(message.content).trimStart() : contentToText(message.content).trim();
  const toolCalls = message.tool_calls ?? [];

  if (!text && toolCalls.length === 0) return null;

  return (
    <Stack
      direction="row"
      spacing={1.25}
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
      alignItems="flex-end"
      sx={animateIn ? { animation: `${rise} 260ms ease-out`, ...noMotion } : undefined}
    >
      {!isUser && (
        <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main' }}>
          <AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />
        </Avatar>
      )}
      <Box sx={{ maxWidth: { xs: '85%', md: '70%' } }}>
        {toolCalls.length > 0 && (
          <Stack direction="row" spacing={0.75} sx={{ mb: text ? 0.75 : 0, flexWrap: 'wrap', rowGap: 0.75 }}>
            {toolCalls.map((tc, i) => (
              <ToolChip key={tc.id ?? i} name={tc.name} status={tc.id ? toolStatuses?.[tc.id] : undefined} />
            ))}
          </Stack>
        )}
        {text && (
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderRadius: 3,
              borderBottomRightRadius: isUser ? 6 : 24,
              borderBottomLeftRadius: isUser ? 24 : 6,
              bgcolor: isUser ? 'primary.main' : 'background.paper',
              color: isUser ? '#fff' : 'text.primary',
              border: isUser ? 'none' : '1px solid',
              borderColor: 'divider',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 14.5,
              lineHeight: 1.6,
              opacity: rejected ? 0.55 : 1,
              transition: 'opacity 250ms ease',
              ...(rejected ? { animation: `${shake} 380ms ease-in-out`, ...noMotion } : {}),
            }}
          >
            {text}
            {streaming && (
              <Box
                component="span"
                aria-hidden
                sx={{
                  display: 'inline-block',
                  width: '2px',
                  height: '1.1em',
                  ml: '1px',
                  verticalAlign: 'text-bottom',
                  bgcolor: 'primary.main',
                  animation: `${blink} 1s step-end infinite`,
                  ...noMotion,
                }}
              />
            )}
          </Box>
        )}
        {isUser && (
          <Fade in={rejected} timeout={250} unmountOnExit>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              justifyContent="flex-end"
              sx={{ mt: 0.5, color: 'error.main' }}
            >
              <ErrorOutlineRoundedIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">Not sent</Typography>
            </Stack>
          </Fade>
        )}
      </Box>
      {isUser && (
        <Avatar sx={{ width: 30, height: 30, bgcolor: 'grey.400' }}>
          <PersonRoundedIcon sx={{ fontSize: 17 }} />
        </Avatar>
      )}
    </Stack>
  );
}

/**
 * Ring beside the composer: the limit closest to running out, with every limit on hover.
 * e.g. a ring 82% full (orange, since it's past 80%) and, on hover:
 *   Assistant credits · Free plan
 *   This week: 90 of 500 left · resets Mon, Oct 5, 5:30 AM
 */
function UsageRing({ usage, tightest }: { usage: ChatUsage; tightest: ChatUsageLimit }) {
  const ratio = usedRatio(tightest);
  const warn = !usage.available || ratio >= USAGE_WARNING_RATIO;
  const percent = Math.floor(ratio * 100);
  const details = (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
        Assistant credits · {usage.plan.name} plan
      </Typography>
      {usage.limits.map((limit) => {
        const reset = formatReset(limit.resets_at);
        return (
          <Typography key={limit.period} variant="caption" component="div">
            {PERIOD_LABELS[limit.period]}: {formatCredits(limit.remaining)} of {formatCredits(limit.limit)} left
            {reset ? ` · resets ${reset}` : ''}
          </Typography>
        );
      })}
    </Box>
  );
  return (
    <Tooltip title={details} placement="top-end" arrow>
      <Box
        tabIndex={0}
        role="img"
        aria-label={`${percent}% of ${PERIOD_CREDITS[tightest.period]} assistant credits used`}
        sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0, borderRadius: '50%', cursor: 'default' }}
      >
        {/* track */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={34}
          thickness={4.5}
          sx={{ color: 'action.hover', position: 'absolute' }}
        />
        <CircularProgress
          variant="determinate"
          value={ratio * 100}
          size={34}
          thickness={4.5}
          color={warn ? 'warning' : 'primary'}
        />
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            fontSize: 9.5,
            fontWeight: 600,
            color: warn ? 'warning.main' : 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {percent}%
        </Typography>
      </Box>
    </Tooltip>
  );
}

const ChatPage: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { showToast } = useToast();

  const [activeThreadId, setActiveThreadId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [localMessages, setLocalMessages] = useState<LocalMessage[]>([]);
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState<ChatThread | null>(null);
  // The text stays set while the alert collapses out, so it doesn't go blank mid-animation.
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendErrorOpen, setSendErrorOpen] = useState(false);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const localIdRef = useRef(0);
  const timersRef = useRef<number[]>([]);
  // The reply being streamed; the ref mirrors it for the send callbacks.
  const [liveSteps, setLiveSteps] = useState<LiveStep[]>([]);
  const liveStepsRef = useRef<LiveStep[]>([]);
  const streamAbortRef = useRef<AbortController | null>(null);

  const threadsQuery = useChatThreads();
  const threadQuery = useChatThread(activeThreadId);
  const sendMessage = useSendChatMessage();
  const deleteThread = useDeleteChatThread();
  const usageQuery = useChatUsage();
  const tightest = tightestLimit(usageQuery.data);
  const blockedReason = usageBlockedReason(usageQuery.data);

  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      streamAbortRef.current?.abort();
    };
  }, []);

  const setLive = (steps: LiveStep[]) => {
    liveStepsRef.current = steps;
    setLiveSteps(steps);
  };

  /** Stop showing the reply being streamed (the server still finishes the turn). */
  const stopStreaming = () => {
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    setLive([]);
  };

  const showSendError = (message: string) => {
    setSendError(message);
    setSendErrorOpen(true);
  };
  const hideSendError = () => setSendErrorOpen(false);

  const setLocalStatus = (localId: number, status: LocalMessage['status']) =>
    setLocalMessages((prev) => prev.map((m) => (m.localId === localId ? { ...m, status } : m)));
  const removeLocal = (localId: number) =>
    setLocalMessages((prev) => prev.filter((m) => m.localId !== localId));

  const serverMessages = useMemo(
    () => (activeThreadId != null ? threadQuery.data?.chat_history ?? [] : []),
    [activeThreadId, threadQuery.data]
  );

  // Drop optimistic messages once the server history includes them. Runs when
  // either side changes: the refetched history can arrive before the local
  // reply is added, and that reply must still be dropped.
  //
  // A local message only matches server messages added after it was sent
  // (index >= its serverCount). e.g. the thread has 4 messages, including a
  // "yes"; the user sends "yes" again (serverCount 4). The old "yes" at index 1
  // isn't its copy, so the new bubble stays until the history has 5+ messages.
  const isInServerHistory = (lm: LocalMessage) =>
    serverMessages
      .slice(lm.serverCount)
      .some((sm) => sm.role === lm.role && contentToText(sm.content) === contentToText(lm.content));

  useEffect(() => {
    setLocalMessages((local) => {
      if (!local.length || !serverMessages.length) return local;
      const kept = local.filter((lm) => !isInServerHistory(lm));
      return kept.length === local.length ? local : kept;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverMessages, localMessages]);

  // Local copies are also filtered out here, not only in the effect above: the
  // effect runs after paint, so the frame where the refetched history arrives
  // would otherwise show the reply twice (and jump the scroll).
  const visibleMessages = useMemo(
    () =>
      [
        ...serverMessages.map((message, i) => ({ key: `s-${i}`, message, local: undefined as LocalMessage | undefined })),
        ...localMessages
          .filter((m) => !isInServerHistory(m))
          .map((m) => ({ key: `l-${m.localId}`, message: m as ChatMessage, local: m })),
      ].filter(({ message }) => message.role === 'user' || message.role === 'assistant'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverMessages, localMessages]
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [visibleMessages.length, sendMessage.isPending]);

  // Follow the streamed reply as it grows, unless the user has scrolled up to read.
  const liveSize = liveSteps.reduce((n, step) => n + step.text.length + step.tools.length, 0);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && liveSize && el.scrollHeight - el.scrollTop - el.clientHeight < 160) {
      el.scrollTop = el.scrollHeight;
    }
  }, [liveSize]);

  const startNewChat = () => {
    stopStreaming();
    setActiveThreadId(null);
    setLocalMessages([]);
    hideSendError();
    setThreadsOpen(false);
  };

  const selectThread = (id: number) => {
    stopStreaming();
    setActiveThreadId(id);
    setLocalMessages([]);
    hideSendError();
    setThreadsOpen(false);
  };

  const handleSend = (text?: string) => {
    const raw = text ?? draft;
    if (raw.length > MAX_MESSAGE_CHARS) {
      showSendError(
        `Your message is too long (${raw.length} characters). Please shorten it to at most ${MAX_MESSAGE_CHARS} characters.`
      );
      return;
    }
    const message = raw.trim();
    if (!message || sendMessage.isPending || blockedReason) return;
    hideSendError();
    setDraft('');
    const localId = ++localIdRef.current;
    const sentAtCount = serverMessages.length;
    setLocalMessages((prev) => [
      // a still-visible rejected message makes way for the new one
      ...prev.map((m) => (m.status === 'rejected' ? { ...m, status: 'leaving' as const } : m)),
      { localId, serverCount: sentAtCount, role: 'user', content: message },
    ]);

    const controller = new AbortController();
    streamAbortRef.current = controller;
    setLive([]);

    sendMessage.mutate(
      {
        payload: { message, ...(activeThreadId != null ? { chat_window_id: activeThreadId } : {}) },
        onEvent: (event) => setLive(applyStreamEvent(liveStepsRef.current, event)),
        signal: controller.signal,
      },
      {
        onSuccess: (result) => {
          // The streamed steps stay on screen as local messages until the
          // refetched history includes them.
          const steps = liveStepsRef.current.length
            ? liveStepsRef.current
            : [{ text: contentToText(result.response), tools: [] }];
          setLocalMessages((prev) => [
            ...prev,
            ...steps.map((step) => ({
              ...liveStepMessage(step),
              localId: ++localIdRef.current,
              serverCount: sentAtCount,
              streamed: true,
            })),
          ]);
          setLive([]);
          if (activeThreadId == null) {
            setActiveThreadId(result.thread_id);
          }
        },
        onError: (err) => {
          if (controller.signal.aborted) return; // the user switched conversation
          setLive([]);
          if (err instanceof ChatStreamError && err.threadId != null) {
            // Failed mid-reply: the message reached the assistant and is in the
            // thread, so it stays; the refetched history shows what was saved.
            showSendError(err.message);
            if (activeThreadId == null) setActiveThreadId(err.threadId);
            return;
          }
          showSendError(getApiErrorMessage(err, 'The assistant could not process your message.'));
          // put the message back in the composer so nothing is lost, mark the
          // bubble "Not sent", then let it collapse away
          setDraft(message);
          setLocalStatus(localId, 'rejected');
          timersRef.current.push(
            window.setTimeout(() => setLocalStatus(localId, 'leaving'), reduceMotion ? 0 : REJECTED_HOLD_MS)
          );
        },
      }
    );
  };

  const confirmDeleteThread = () => {
    if (!threadToDelete) return;
    const id = threadToDelete.thread_id;
    deleteThread.mutate(id, {
      onSuccess: () => {
        setThreadToDelete(null);
        if (activeThreadId === id) startNewChat();
        showToast('Conversation deleted');
      },
      onError: (err) => {
        setThreadToDelete(null);
        showToast(getApiErrorMessage(err), 'error');
      },
    });
  };

  const threadsPane = (
    <Box
      sx={{
        width: 290,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 2 }}>
        <Button fullWidth variant="contained" startIcon={<AddRoundedIcon />} onClick={startNewChat}>
          New chat
        </Button>
      </Box>
      <Typography variant="overline" sx={{ px: 2.5, color: 'text.secondary' }}>
        Conversations
      </Typography>
      <List sx={{ flexGrow: 1, overflowY: 'auto', px: 1, pb: 1 }}>
        {threadsQuery.isLoading ? (
          [...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={56} sx={{ mx: 1, my: 0.75, borderRadius: 2 }} />
          ))
        ) : threadsQuery.isError ? (
          <Alert severity="error" sx={{ m: 1 }}>
            Could not load conversations.
          </Alert>
        ) : (threadsQuery.data ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
            No conversations yet — start one!
          </Typography>
        ) : (
          (threadsQuery.data ?? []).map((thread) => (
            <ListItem
              key={thread.thread_id}
              disablePadding
              secondaryAction={
                <Tooltip title="Delete conversation">
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => setThreadToDelete(thread)}
                    aria-label="Delete conversation"
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              sx={{ '& .MuiListItemSecondaryAction-root': { right: 12 } }}
            >
              <ListItemButton
                selected={thread.thread_id === activeThreadId}
                onClick={() => selectThread(thread.thread_id)}
                sx={{ borderRadius: 2, my: 0.25, pr: 6 }}
              >
                <ListItemText
                  primary={threadTitle(thread)}
                  secondary={formatRelativeDay(thread.updated_at)}
                  primaryTypographyProps={{ noWrap: true, fontSize: 14, fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: 12 }}
                />
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>
    </Box>
  );

  const showWelcome = activeThreadId == null && visibleMessages.length === 0;
  const lastStep = liveSteps[liveSteps.length - 1];
  const awaitingModel = !lastStep || (lastStep.tools.length > 0 && lastStep.tools.every((t) => t.status !== 'running'));

  return (
    // Lock to the viewport below the sticky app bar (56px on xs, 64px from sm up)
    // so the message list scrolls internally instead of the whole document.
    <Box
      sx={{
        display: 'flex',
        minHeight: 0,
        height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
      }}
    >
      {isDesktop ? (
        threadsPane
      ) : (
        <Drawer open={threadsOpen} onClose={() => setThreadsOpen(false)}>
          {threadsPane}
        </Drawer>
      )}

      {/* Conversation column */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
        {!isDesktop && (
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Button startIcon={<HistoryRoundedIcon />} onClick={() => setThreadsOpen(true)} size="small">
              Conversations
            </Button>
          </Box>
        )}

        {/* Messages */}
        <Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
          {/* Skeleton only when there's nothing to show yet: a new chat's first reply
              is already on screen while its thread loads in the background. */}
          {activeThreadId != null && threadQuery.isLoading && visibleMessages.length === 0 ? (
            <Stack spacing={2} sx={{ maxWidth: 860, mx: 'auto' }}>
              <Skeleton variant="rounded" height={48} width="60%" sx={{ borderRadius: 3 }} />
              <Skeleton variant="rounded" height={48} width="70%" sx={{ borderRadius: 3, alignSelf: 'flex-end' }} />
              <Skeleton variant="rounded" height={48} width="50%" sx={{ borderRadius: 3 }} />
            </Stack>
          ) : showWelcome ? (
            <Fade in appear timeout={reduceMotion ? 0 : 400}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  maxWidth: 640,
                  mx: 'auto',
                }}
              >
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', mb: 2 }}>
                  <AutoAwesomeRoundedIcon />
                </Avatar>
                <Typography variant="h5" gutterBottom>
                  How can I help you today?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  I can create and manage your tasks, categories and time logs — just ask in plain language.
                </Typography>
                <Stack spacing={1} sx={{ width: '100%' }}>
                  {SUGGESTIONS.map((s) => (
                    <Button
                      key={s}
                      variant="outlined"
                      color="inherit"
                      disabled={!!blockedReason}
                      onClick={() => handleSend(s)}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        color: 'text.secondary',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      {s}
                    </Button>
                  ))}
                </Stack>
              </Box>
            </Fade>
          ) : (
            <Stack sx={{ maxWidth: 860, mx: 'auto' }}>
              {/* Spacing is padding inside each Collapse (not Stack spacing), so a
                  collapsing message takes its gap with it instead of jumping at the end. */}
              {visibleMessages.map(({ key, message, local }) => (
                <Collapse
                  key={key}
                  in={local?.status !== 'leaving'}
                  timeout={reduceMotion ? 0 : COLLAPSE_MS}
                  onExited={local ? () => removeLocal(local.localId) : undefined}
                >
                  <Box sx={{ pb: 2 }}>
                    <MessageBubble
                      message={message}
                      rejected={!!local?.status}
                      animateIn={!!local && !local.streamed}
                    />
                  </Box>
                </Collapse>
              ))}
              {/* the reply being streamed */}
              {liveSteps.map((step, i) => (
                <Box key={`live-${i}`} sx={{ pb: 2 }}>
                  <MessageBubble
                    message={liveStepMessage(step)}
                    animateIn
                    toolStatuses={Object.fromEntries(step.tools.map((t) => [t.id, t.status]))}
                    streaming={i === liveSteps.length - 1 && !step.tools.length}
                  />
                </Box>
              ))}
              {/* dots while waiting for the model: before its first token, and after its tools finish */}
              <Collapse in={sendMessage.isPending && awaitingModel} timeout={reduceMotion ? 0 : 220} unmountOnExit>
                <Stack direction="row" spacing={1.25} alignItems="flex-end" sx={{ pb: 2 }}>
                  <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main' }}>
                    <AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                  <Box
                    sx={{
                      borderRadius: 3,
                      borderBottomLeftRadius: 6,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <TypingIndicator />
                  </Box>
                </Stack>
              </Collapse>
              <div ref={bottomRef} />
            </Stack>
          )}
        </Box>

        {/* Composer */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            pb: { xs: 2, md: 3 },
            pt: 1,
          }}
        >
          <Box sx={{ maxWidth: 860, mx: 'auto' }}>
            <Collapse in={sendErrorOpen} timeout={reduceMotion ? 0 : 260} onExited={() => setSendError(null)}>
              {sendError && (
                <Alert severity="error" sx={{ mb: 1.5, whiteSpace: 'pre-line' }} onClose={hideSendError}>
                  {sendError}
                </Alert>
              )}
            </Collapse>
            {blockedReason && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {blockedReason}
              </Alert>
            )}
            <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              fullWidth
              multiline
              maxRows={6}
              disabled={!!blockedReason}
              placeholder="Message the assistant… (Enter to send, Shift+Enter for a new line)"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              InputProps={{
                sx: { borderRadius: 4, bgcolor: 'background.paper', pr: 1 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      color="primary"
                      onClick={() => handleSend()}
                      disabled={
                        !draft.trim() || sendMessage.isPending || draft.length > MAX_MESSAGE_CHARS || !!blockedReason
                      }
                      aria-label="Send message"
                    >
                      {sendMessage.isPending ? <CircularProgress size={22} /> : <SendRoundedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {usageQuery.data && tightest && <UsageRing usage={usageQuery.data} tightest={tightest} />}
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 0.75, px: 0.5, alignItems: 'baseline', justifyContent: 'space-between' }}
            >
              <Typography variant="caption" color="text.secondary">
                The assistant can create, update and delete your data — review important changes.
              </Typography>
              <Typography
                variant="caption"
                color={draft.length > MAX_MESSAGE_CHARS ? 'error' : 'text.secondary'}
                sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
              >
                {draft.length.toLocaleString()} / {MAX_MESSAGE_CHARS.toLocaleString()}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>

      <ConfirmDialog
        open={!!threadToDelete}
        title="Delete conversation?"
        message={
          <>“{threadToDelete ? threadTitle(threadToDelete) : ''}” and its history will be permanently deleted.</>
        }
        loading={deleteThread.isPending}
        onConfirm={confirmDeleteThread}
        onCancel={() => setThreadToDelete(null)}
      />
    </Box>
  );
};

export default ChatPage;
